import React, { useEffect, useState } from 'react';
import { Box, Checkbox, FormHelperText, Grid } from '@mui/material';

const CheckBoxComponent = ({
  name,
  question,
  value,
  onChange,
  onNext,
  setValidationState,
  registerValidation,
  isMobile,
}) => {
  const [checkedValues, setCheckedValues] = useState(
    Array.isArray(value) ? value : []
  );
  const [error, setError] = useState('');

  useEffect(() => {
    setCheckedValues(Array.isArray(value) ? value : []);
  }, [value]);

  const validate = () => {
    let isValid = true;

    if (question?.validation?.required && checkedValues.length === 0) {
      isValid = false;
      setError(question?.validation?.message || 'Please select at least one');
    } else {
      setError('');
    }

    setValidationState?.(name, isValid);
    return isValid;
  };

  useEffect(() => {
    if (registerValidation) {
      registerValidation(name, () => validate());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkedValues]);

  const handleToggle = (option) => {
    const newValues = checkedValues.includes(option)
      ? checkedValues.filter((v) => v !== option)
      : [...checkedValues, option];

    setCheckedValues(newValues);
    onChange?.(name, newValues);

    const isValid = question?.validation?.required
      ? newValues.length > 0
      : true;
    if (isValid && !question?.disableAutoNext) {
      onNext?.();
    }
  };

  const isOneCol = Boolean(question?.column);
  const options = Array.isArray(question?.options) ? question.options : [];

  const UncheckedIcon = (
    <Box
      aria-hidden
      sx={{
        width: 16,
        height: 16,
        border: '1px solid #A2ACB1',
        borderRadius: '6px',
        boxSizing: 'border-box',
        backgroundColor: 'transparent',
      }}
    />
  );

  const CheckedIcon = (
    <Box
      aria-hidden
      sx={{
        width: 16,
        height: 16,
        border: '1px solid #D9D9D9',
        borderRadius: '6px',
        boxSizing: 'border-box',
        backgroundColor: '#C7CA00',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <svg
        width="12"
        height="10"
        viewBox="0 0 12 10"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M1.5 5.5L4.5 8.5L10.5 1.5"
          stroke="#162F3B"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </Box>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        mt: 0,
      }}
    >
      <Grid
        container
        spacing={2}
        justifyContent="center"
        direction={isOneCol ? 'column' : 'row'}
      >
        {options.map((option, index) => {
          const isChecked = checkedValues.includes(option);
          return (
            <Grid
              item
              key={`${option}-${index}`}
              xs={12}
              sm={isOneCol ? 12 : 6}
              md={isOneCol ? 12 : 6}
              lg={isOneCol ? 12 : 6}
              sx={{ display: 'flex', justifyContent: 'center' }}
            >
              <Box
                onClick={() => handleToggle(option)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  gap: isMobile ? '12px' : '8px',
                  width: isMobile ? '80vw' : isOneCol ? '100%' : '360px',
                  maxWidth: isOneCol ? 720 : 'unset',
                  minHeight: '48px',
                  borderRadius: '20px',
                  border: 'none',
                  backgroundColor: 'transparent',
                  px: isMobile ? '24px' : '8px',
                  py: '12px',
                  cursor: 'pointer',
                  fontFamily: 'Inter, sans-serif',
                  boxSizing: 'border-box',
                }}
              >
                <Checkbox
                  checked={isChecked}
                  icon={UncheckedIcon}
                  checkedIcon={CheckedIcon}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleToggle(option);
                  }}
                  disableRipple
                  sx={{
                    p: 0,
                    width: 24,
                    height: 24,
                    '&:hover': { backgroundColor: 'transparent' },
                  }}
                  inputProps={{ 'aria-label': option }}
                />

                <Box
                  component="span"
                  sx={{
                    flex: 1,
                    textAlign: 'left',
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 600,
                    fontSize: isMobile ? '1rem' : '0.9375rem',
                    lineHeight: isMobile ? '1.5rem' : '1.375rem',
                    letterSpacing: '-0.002em',
                    color: '#738289',
                  }}
                >
                  {option}
                </Box>
              </Box>
            </Grid>
          );
        })}
      </Grid>

      {error && (
        <FormHelperText
          sx={{
            textAlign: 'right',
            fontFamily: 'Inter, sans-serif',
            fontSize: isMobile ? '0.6rem' : '0.75rem',
            color: '#D32F2F',
            mt: '4px',
            pr: '8px',
          }}
        >
          {error}
        </FormHelperText>
      )}
    </Box>
  );
};

export default CheckBoxComponent;
